import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconCarouselArrowLeft from "../../../../img/icons/carousel-arrow-left.svg";
import iconCarouselArrowRight from "../../../../img/icons/carousel-arrow-right.svg";
import { RichText } from "../../../components/RichText";
import { IProductID, ISafeHTML, WebPageURL } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IProductTileAttributeRow,
    ITileID,
} from "../../../models/product-compare";
import { BreakPoint, IViewport } from "../../../models/screen";
import { ProductCompareTileAttributeRow } from "../ProductCompareTileAttributeRow";
import { ProductCompareTileHeader } from "../ProductCompareTileHeader";
import { ProductCompareAnalyticsProvider } from "../analytics";
import { useViewItemListTracking } from "../hooks/useViewItemListTracking";
import { getSelectedVariant } from "../selectors";

type IProps = Immutable<{
    header: ISafeHTML;
    tiles: ICompareTile[];
    basketLink?: WebPageURL;
    financingLink?: WebPageURL;
    preselectedSize?: string | null;
    sizeAttributeOptionGroup?: IAttributeOptionGroup | null;
    priceSelectValue: string;
    pageRootProduct?: IProductID | null;
    returnUrl?: string;
    viewport: IViewport;
}>;

const calculateTilesPerSlide = (viewportWidth: number): number => {
    return viewportWidth > BreakPoint.LARGE ? 5 : 2;
};

const buildViewableTiles = (
    selectedTiles: readonly ICompareTile[],
    currentPosition: number,
    tilesPerSlide: number,
): ICompareTile[] => {
    const increment = currentPosition + tilesPerSlide;

    if (selectedTiles.length - currentPosition === 1) {
        return selectedTiles.slice(currentPosition - 1, increment);
    }
    return selectedTiles.slice(currentPosition, increment);
};

type HeaderContent = {
    header: ISafeHTML;
    linkHref: string | null;
};

const buildHeaderContent = (
    header: ISafeHTML,
    returnUrl: string | undefined,
    selectedTileIDs: readonly ITileID[],
): HeaderContent => {
    if (!returnUrl) {
        return { header, linkHref: null };
    }

    let href = returnUrl;
    if (selectedTileIDs.length > 0) {
        const separator = href.includes("?") ? "&" : "?";
        href = `${href}${separator}tiles=${encodeURIComponent(selectedTileIDs.join(","))}`;
    }
    return { header, linkHref: href };
};

const renderHeaderContent = (content: HeaderContent): React.ReactNode => {
    if (content.linkHref) {
        return (
            <>
                <RichText html={content.header} inline={true} />
                <a
                    className="al-compareresults-changeselection"
                    href={content.linkHref}
                >
                    {t`Add/Change Selection`}
                </a>
            </>
        );
    }
    return <RichText html={content.header} />;
};

const buildAttributeRow = (
    tile: ICompareTile,
    row: IProductTileAttributeRow,
    basketLink: WebPageURL | undefined,
    financingLink: WebPageURL | undefined,
    priceSelectValue: string,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return (
        <ProductCompareTileAttributeRow
            key={`${tile.product_compare_tile.id}-${row.row_type.id}`}
            row={row}
            data={tile}
            basketLink={basketLink}
            financingLink={financingLink}
            priceSelectValue={priceSelectValue}
            sizeAttributeOptionGroup={sizeAttributeOptionGroup || null}
        />
    );
};

const buildChildRows = (
    tile: ICompareTile,
    rows: ReadonlyArray<IProductTileAttributeRow>,
    basketLink: WebPageURL | undefined,
    financingLink: WebPageURL | undefined,
    priceSelectValue: string,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return rows.map((row: IProductTileAttributeRow) => {
        return (
            <div
                className={`child-row product-attribute-row ${row.row_type.row_type}`}
                key={`${row.row_type.id}-child-row`}
            >
                {buildAttributeRow(
                    tile,
                    row,
                    basketLink,
                    financingLink,
                    priceSelectValue,
                    sizeAttributeOptionGroup,
                )}
            </div>
        );
    });
};

const buildAttributeRows = (
    tile: ICompareTile,
    basketLink: WebPageURL | undefined,
    financingLink: WebPageURL | undefined,
    priceSelectValue: string,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return tile.rows.map((row: IProductTileAttributeRow) => {
        return (
            <div
                key={row.row_type.id}
                className={`product-attribute-row ${row.row_type.row_type}`}
            >
                <h4>{row.row_type.title}</h4>
                <div>
                    {buildAttributeRow(
                        tile,
                        row,
                        basketLink,
                        financingLink,
                        priceSelectValue,
                        sizeAttributeOptionGroup,
                    )}
                    {buildChildRows(
                        tile,
                        row.children,
                        basketLink,
                        financingLink,
                        priceSelectValue,
                        sizeAttributeOptionGroup,
                    )}
                </div>
            </div>
        );
    });
};

const buildCompareTiles = (
    viewableTiles: ICompareTile[],
    priceSelectValue: string,
    pageRootProduct: IProductID | null | undefined,
    basketLink: WebPageURL | undefined,
    financingLink: WebPageURL | undefined,
    sizeAttributeOptionGroup: IAttributeOptionGroup | null | undefined,
): React.ReactNode => {
    return viewableTiles.map((tile: ICompareTile) => {
        const variant = getSelectedVariant(tile, priceSelectValue);
        return (
            <div className="tile" key={`${tile.product_compare_tile.id}`}>
                <ProductCompareTileHeader
                    data={tile}
                    selectedVariant={variant}
                    pageRootProduct={pageRootProduct || null}
                    showRating={false}
                />
                {buildAttributeRows(
                    tile,
                    basketLink,
                    financingLink,
                    priceSelectValue,
                    sizeAttributeOptionGroup,
                )}
            </div>
        );
    });
};

const renderSlideControls = (
    viewportWidth: number,
    selectedTilesLength: number,
    currentPosition: number,
    tilesPerSlide: number,
    onPrevSlideClick: () => void,
    onNextSlideClick: () => void,
): React.ReactNode => {
    if (viewportWidth > BreakPoint.LARGE) {
        return null;
    }
    if (selectedTilesLength <= 2) {
        return null;
    }
    return (
        <nav>
            <button
                aria-label={t`Previous`}
                disabled={currentPosition === 0}
                onClick={onPrevSlideClick}
            >
                <SVG aria-hidden="true" src={iconCarouselArrowLeft} />
            </button>
            <button
                aria-label={t`next`}
                disabled={
                    currentPosition + tilesPerSlide >= selectedTilesLength
                }
                onClick={onNextSlideClick}
            >
                <SVG aria-hidden="true" src={iconCarouselArrowRight} />
            </button>
        </nav>
    );
};

export const ProductCompareGrid__ColumnCard_Component = (
    props: IProps,
): React.ReactNode => {
    const [currentPosition, setCurrentPosition] = React.useState(0);
    const selectedTileIDs = React.useMemo(
        () => props.tiles.map((tile) => tile.product_compare_tile.id),
        [props.tiles],
    );
    const selectedTiles = props.tiles;
    const [tilesPerSlide, setTilesPerSlide] = React.useState(
        calculateTilesPerSlide(props.viewport.width),
    );

    const compareTiles = React.useRef<HTMLDivElement>(null);

    // Handle viewport resize
    React.useEffect(() => {
        const _window = getWindow();
        if (!_window) {
            return;
        }

        const handleResize = (): void => {
            setTilesPerSlide(calculateTilesPerSlide(props.viewport.width));
        };

        handleResize();
        _window.addEventListener("resize", handleResize);
        return () => {
            _window.removeEventListener("resize", handleResize);
        };
    }, [props.viewport.width]);

    const onPrevSlideClick = (): void => {
        setCurrentPosition(currentPosition - 1);
    };

    const onNextSlideClick = (): void => {
        setCurrentPosition(currentPosition + 1);
    };

    const viewableTiles = buildViewableTiles(
        selectedTiles,
        currentPosition,
        tilesPerSlide,
    );
    const { listMetadata, viewedProducts } = useViewItemListTracking(
        viewableTiles,
        props.priceSelectValue,
        props.header,
        "Product Compare Grid Column Card",
        selectedTileIDs.length,
    );

    return (
        <ProductCompareAnalyticsProvider
            listMetadata={listMetadata}
            viewedProducts={viewedProducts}
        >
            <header className="product-compare-grid-block-header">
                {renderHeaderContent(
                    buildHeaderContent(
                        props.header,
                        props.returnUrl,
                        selectedTileIDs,
                    ),
                )}
            </header>
            {renderSlideControls(
                props.viewport.width,
                selectedTiles.length,
                currentPosition,
                tilesPerSlide,
                onPrevSlideClick,
                onNextSlideClick,
            )}
            <div className="product-compare-tiles" ref={compareTiles}>
                {buildCompareTiles(
                    viewableTiles,
                    props.priceSelectValue,
                    props.pageRootProduct,
                    props.basketLink,
                    props.financingLink,
                    props.sizeAttributeOptionGroup,
                )}
            </div>
        </ProductCompareAnalyticsProvider>
    );
};
